import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { LanguageProvider, useLanguage, Language } from './contexts/LanguageContext';
import { NoticeBar } from './components/NoticeBar';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { FloatingContact } from './components/ContactButtons';
import { Home } from './pages/Home';
import { Contact } from './pages/Contact';
import { PricingPage } from './pages/PricingPage';
import { HowItWorksPage } from './pages/HowItWorksPage';
import { FAQPage } from './pages/FAQPage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { TermsOfServicePage } from './pages/TermsOfServicePage';
import { RefundPolicyPage } from './pages/RefundPolicyPage';

// Whenever the route (page) changes, jump back to the top. Without this, a SPA
// keeps the previous scroll position, so a new page opens scrolled down instead
// of at its top. We reset both window and the scrolling element to be safe
// across browsers.
function ScrollToTop() {
  const {
    pathname
  } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
  }, [pathname]);
  return null;
}

// "Skip to main content" link — the first thing a keyboard / screen-reader user
// reaches. It stays visually hidden until focused, then jumps past the nav
// straight to the page content.
const SKIP_LABEL: Record<Language, string> = {
  en: 'Skip to main content',
  es: 'Saltar al contenido principal',
  ht: 'Ale dirèkteman nan kontni prensipal la',
  vi: 'Chuyển đến nội dung chính'
};
function SkipLink() {
  const {
    language
  } = useLanguage();
  return <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[100] focus:px-4 focus:py-2 focus:rounded-full focus:bg-brand-textPrimary focus:text-white focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-brand-accent focus:ring-offset-2" data-id="element-0" data-mp="3dm6064fiusje">
      {SKIP_LABEL[language]}
    </a>;
}
export function App() {
  return <LanguageProvider data-id="element-1" data-mp="3uc9i9u1ffii8">
      <BrowserRouter data-id="element-2" data-mp="3uf5ijp0qyj9u">
        <ScrollToTop data-id="element-3" data-mp="d4e4tqw8j8vn" />
        <div className="min-h-screen flex flex-col bg-brand-bg font-sans text-brand-textPrimary" data-id="element-4" data-mp="d4xmd6hz3v95">
          <SkipLink data-id="element-5" data-mp="85b8cuubmaza" />
          {/* Legal disclosure: shown at the very top of the page but allowed to
              scroll away normally — it is NOT pinned, so it doesn't eat the
              screen (especially on mobile). */}
          <NoticeBar data-id="element-6" data-mp="3bnus18yxo8ih" />
          {/* Only the compact header stays pinned at the top while scrolling. */}
          <div className="sticky top-0 z-50" data-id="element-7" data-mp="3qcin7e4vag7w">
            <Header data-id="element-8" data-mp="3ljxtsdpvi54w" />
          </div>
          <main id="main-content" tabIndex={-1} className="flex-grow focus:outline-none" data-id="element-9" data-mp="3liwkadoi9qpa">
            <Routes data-id="element-10" data-mp="2svto3s7axs27">
              <Route path="/" element={<Home data-id="element-12" data-mp="2suqv9eqf5fjn" />} data-id="element-11" data-mp="2swbuhhnohmnu" />
              <Route path="/how-it-works" element={<HowItWorksPage data-id="element-14" data-mp="18e1axny9tulm" />} data-id="element-13" data-mp="2svjyi5q65mx1" />
              <Route path="/pricing" element={<PricingPage data-id="element-16" data-mp="18boi2fqz5zts" />} data-id="element-15" data-mp="18czm8goc19lf" />
              <Route path="/faq" element={<FAQPage data-id="element-18" data-mp="1d5jac7knbn4h" />} data-id="element-17" data-mp="1d47ydkh1r92a" />
              <Route path="/contact" element={<Contact data-id="element-20" data-mp="yflkln72ipd6" />} data-id="element-19" data-mp="1d5ssj8zg8pzm" />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage data-id="element-22" data-mp="yheowvu7kxi0" />} data-id="element-21" data-mp="ygd4w1v7t0o5" />
              <Route path="/terms-of-service" element={<TermsOfServicePage data-id="element-24" data-mp="13cllljp291xf" />} data-id="element-23" data-mp="13eew5a2so6ap" />
              <Route path="/refund-policy" element={<RefundPolicyPage data-id="element-26" data-mp="1r2698fw5snd1" />} data-id="element-25" data-mp="1r5akfx81kjd7" />
            </Routes>
          </main>
          <Footer data-id="element-27" data-mp="1w2122ozhvn0q" />
          {/* Always-visible Call + WhatsApp buttons */}
          <FloatingContact data-id="element-28" data-mp="1i4ccx4ye9tpv" />
        </div>
      </BrowserRouter>
    </LanguageProvider>;
}